import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface MdtBottomBarProps {
  callCardNotClosed?: boolean;
  isEditMode?: boolean;
  isSelfOnCall?: boolean;
  mdtOfficerTimeButtonsEnabled?: boolean;
  enrouteDisabled?: boolean;
  onSceneDisabled?: boolean;
  clearedDisabled?: boolean;
  classEnrouteBtn?: string | Record<string, boolean>;
  classOnSceneBtn?: string | Record<string, boolean>;
  classClearedBtn?: string | Record<string, boolean>;
  canEdit?: boolean;
  hasCloseCallCardPermissions?: boolean;
  hasDisposition?: boolean;
  onSelfAssign?: () => void;
  onEnRoute?: () => void;
  onOnScene?: () => void;
  onCleared?: () => void;
  onCreateReport?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onUpdate?: () => void;
  onClose?: () => void;
  onShowCloseModal?: () => void;
  onNavigation?: () => void;
  onNavigationSettings?: () => void;
}

export function MdtBottomBar({
  callCardNotClosed = true,
  isEditMode = false,
  isSelfOnCall = false,
  mdtOfficerTimeButtonsEnabled = false,
  enrouteDisabled = false,
  onSceneDisabled = false,
  clearedDisabled = false,
  classEnrouteBtn = '',
  classOnSceneBtn = '',
  classClearedBtn = '',
  canEdit = false,
  hasCloseCallCardPermissions = false,
  hasDisposition = false,
  onSelfAssign,
  onEnRoute,
  onOnScene,
  onCleared,
  onCreateReport,
  onEdit,
  onCancel,
  onUpdate,
  onClose,
  onShowCloseModal,
  onNavigation,
  onNavigationSettings,
}: MdtBottomBarProps) {
  const { theme } = useTheme();

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const containerRef = useRef<HTMLElement>(null);

  // Click outside handler for more menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showMoreMenu) return;
      const moreMenuContainer = containerRef.current?.querySelector('.more-menu-container');
      if (moreMenuContainer && !moreMenuContainer.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMoreMenu]);

  const isEnroute = useMemo((): boolean => {
    if (typeof classEnrouteBtn === 'object') {
      return (classEnrouteBtn as Record<string, boolean>).MDTResponseEnroute === true;
    }
    return String(classEnrouteBtn).includes('MDTResponseEnroute');
  }, [classEnrouteBtn]);

  const isOnScene = useMemo((): boolean => {
    if (typeof classOnSceneBtn === 'object') {
      return (classOnSceneBtn as Record<string, boolean>).MDTResponseOnScene === true;
    }
    return String(classOnSceneBtn).includes('MDTResponseOnScene');
  }, [classOnSceneBtn]);

  const isCleared = useMemo((): boolean => {
    if (typeof classClearedBtn === 'object') {
      return (classClearedBtn as Record<string, boolean>).MDTResponseCleared === true;
    }
    return String(classClearedBtn).includes('MDTResponseCleared');
  }, [classClearedBtn]);

  const currentState = useMemo((): string => {
    if (isEditMode) return 'editing';
    if (!callCardNotClosed) return 'closed';
    if (isCleared) return 'cleared';
    if (!isSelfOnCall) return 'not-assigned';
    if (isOnScene) return 'on-scene';
    if (isEnroute) return 'en-route';
    return 'assigned';
  }, [isEditMode, callCardNotClosed, isCleared, isSelfOnCall, isOnScene, isEnroute]);

  const isReadyToClose = useMemo((): boolean => {
    return isCleared && hasDisposition && hasCloseCallCardPermissions;
  }, [isCleared, hasDisposition, hasCloseCallCardPermissions]);

  const handleEnRoute = useCallback(() => {
    if (!enrouteDisabled) onEnRoute?.();
  }, [enrouteDisabled, onEnRoute]);

  const handleOnScene = useCallback(() => {
    if (!onSceneDisabled) onOnScene?.();
  }, [onSceneDisabled, onOnScene]);

  const handleCleared = useCallback(() => {
    if (!clearedDisabled) onCleared?.();
  }, [clearedDisabled, onCleared]);

  const toggleMoreMenu = useCallback(() => {
    setShowMoreMenu((prev) => !prev);
  }, []);

  const closeMoreMenu = useCallback(() => {
    setShowMoreMenu(false);
  }, []);

  const handleNavigationSettings = useCallback(() => {
    onNavigationSettings?.();
    closeMoreMenu();
  }, [onNavigationSettings, closeMoreMenu]);

  const handleCreateReportFromMenu = useCallback(() => {
    onCreateReport?.();
    closeMoreMenu();
  }, [onCreateReport, closeMoreMenu]);

  return (
    <nav className={`mdt-bottom-bar state-${currentState} ${theme}`} ref={containerRef}>
      <div className="bar-container">

        {/* STATE: Not Assigned */}
        {currentState === 'not-assigned' && (
          <>
            <button type="button" className="bar-btn bar-btn-primary bar-btn-large" onClick={onSelfAssign}>
              <i className="fas fa-user-plus"></i>
              <span className="btn-text">SELF ASSIGN</span>
              <span className="btn-shortcut">Alt+F1</span>
            </button>
            <div className="bar-spacer"></div>
          </>
        )}

        {/* STATE: Assigned (not yet en-route) */}
        {currentState === 'assigned' && (
          <>
            <button
              type="button"
              className={`bar-btn bar-btn-enroute bar-btn-large ${enrouteDisabled ? 'bar-btn-disabled' : ''}`}
              disabled={enrouteDisabled}
              onClick={handleEnRoute}
            >
              <i className="fas fa-car"></i>
              <span className="btn-text">EN-ROUTE</span>
              <span className="btn-shortcut">Alt+F2</span>
            </button>
            <div className="bar-spacer"></div>
            <button type="button" className="bar-btn bar-btn-secondary bar-btn-report" onClick={onCreateReport}>
              <i className="fas fa-file-alt"></i>
              <span className="btn-text">Report</span>
            </button>
          </>
        )}

        {/* STATE: En-Route (active) */}
        {currentState === 'en-route' && (
          <>
            <div className="status-indicator status-enroute">
              <i className="fas fa-car"></i>
              <span>EN-ROUTE</span>
            </div>
            <button
              type="button"
              className={`bar-btn bar-btn-onscene bar-btn-large ${onSceneDisabled ? 'bar-btn-disabled' : ''}`}
              disabled={onSceneDisabled}
              onClick={handleOnScene}
            >
              <i className="fas fa-map-marker-alt"></i>
              <span className="btn-text">ON SCENE</span>
              <span className="btn-shortcut">Alt+F3</span>
            </button>
            <div className="bar-spacer"></div>
            <button type="button" className="bar-btn bar-btn-secondary bar-btn-report" onClick={onCreateReport}>
              <i className="fas fa-file-alt"></i>
              <span className="btn-text">Report</span>
            </button>
          </>
        )}

        {/* STATE: On Scene (active) */}
        {currentState === 'on-scene' && (
          <>
            <div className="status-indicator status-onscene">
              <i className="fas fa-map-marker-alt"></i>
              <span>ON SCENE</span>
            </div>
            <button
              type="button"
              className={`bar-btn bar-btn-cleared bar-btn-large ${clearedDisabled ? 'bar-btn-disabled' : ''}`}
              disabled={clearedDisabled}
              onClick={handleCleared}
            >
              <i className="fas fa-check-circle"></i>
              <span className="btn-text">CLEARED</span>
              <span className="btn-shortcut">Alt+F5</span>
            </button>
            <div className="bar-spacer"></div>
            <button type="button" className="bar-btn bar-btn-secondary bar-btn-report" onClick={onCreateReport}>
              <i className="fas fa-file-alt"></i>
              <span className="btn-text">Report</span>
            </button>
          </>
        )}

        {/* STATE: Cleared */}
        {currentState === 'cleared' && (
          <>
            <div className="status-indicator status-cleared">
              <i className="fas fa-check-circle"></i>
              <span>CLEARED</span>
            </div>
            {hasCloseCallCardPermissions && (
              <button
                type="button"
                className="bar-btn bar-btn-close-call bar-btn-large"
                onClick={onShowCloseModal}
              >
                <i className="fas fa-lock"></i>
                <span className="btn-text">CLOSE CALL</span>
              </button>
            )}
            <div className="bar-spacer"></div>
            <button type="button" className="bar-btn bar-btn-secondary bar-btn-report" onClick={onCreateReport}>
              <i className="fas fa-file-alt"></i>
              <span className="btn-text">Report</span>
            </button>
          </>
        )}

        {/* STATE: Editing */}
        {currentState === 'editing' && (
          <>
            <button type="button" className="bar-btn bar-btn-cancel" onClick={onCancel}>
              <i className="fas fa-times"></i>
              <span className="btn-text">Cancel</span>
            </button>
            <div className="bar-spacer"></div>
            {isReadyToClose ? (
              <>
                <button type="button" className="bar-btn bar-btn-secondary" onClick={onUpdate}>
                  <i className="fas fa-save"></i>
                  <span className="btn-text">Update</span>
                </button>
                <button type="button" className="bar-btn bar-btn-danger bar-btn-large" onClick={onClose}>
                  <i className="fas fa-lock"></i>
                  <span className="btn-text">CLOSE CALL</span>
                </button>
              </>
            ) : (
              <>
                <button type="button" className="bar-btn bar-btn-success bar-btn-large" onClick={onUpdate}>
                  <i className="fas fa-save"></i>
                  <span className="btn-text">UPDATE</span>
                </button>
                {hasCloseCallCardPermissions && (
                  <button type="button" className="bar-btn bar-btn-danger" onClick={onClose}>
                    <i className="fas fa-lock"></i>
                    <span className="btn-text">Close</span>
                  </button>
                )}
              </>
            )}
          </>
        )}

        {/* STATE: Closed */}
        {currentState === 'closed' && (
          <>
            <div className="status-indicator status-closed">
              <i className="fas fa-lock"></i>
              <span>CALL CLOSED</span>
            </div>
            <div className="bar-spacer"></div>
            <button type="button" className="bar-btn bar-btn-secondary bar-btn-report" onClick={onCreateReport}>
              <i className="fas fa-file-alt"></i>
              <span className="btn-text">Report</span>
            </button>
          </>
        )}

        {/* Right Actions (Edit & Nav always visible when not editing) */}
        {currentState !== 'editing' && (
          <div className="right-actions">
            {canEdit && (
              <button type="button" className="bar-btn bar-btn-edit" onClick={onEdit}>
                <i className="fas fa-pencil-alt"></i>
                <span className="btn-text">Edit</span>
              </button>
            )}
            <button type="button" className="bar-btn bar-btn-nav" onClick={onNavigation}>
              <i className="fas fa-route"></i>
              <span className="btn-text">Nav</span>
            </button>
            {/* Nav Settings - desktop only */}
            <button
              type="button"
              className="bar-btn bar-btn-icon bar-btn-nav-settings"
              onClick={handleNavigationSettings}
              title="Navigation Settings"
            >
              <i className="fas fa-cog"></i>
            </button>
            {/* Overflow menu - mobile only */}
            <div className="more-menu-container">
              <button
                type="button"
                className="bar-btn bar-btn-more"
                onClick={(e) => { e.stopPropagation(); toggleMoreMenu(); }}
                title="More options"
              >
                <i className="fas fa-ellipsis-v"></i>
              </button>
              {showMoreMenu && (
                <div className="more-menu-dropdown">
                  <button type="button" className="more-menu-item" onClick={handleCreateReportFromMenu}>
                    <i className="fas fa-file-alt"></i>
                    <span>Report</span>
                  </button>
                  <button type="button" className="more-menu-item" onClick={handleNavigationSettings}>
                    <i className="fas fa-cog"></i>
                    <span>Nav Settings</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </nav>
  );
}

(window as any).MdtBottomBarReact = MdtBottomBar;
